/**
 * agentLinks (gbp-agent): maps health aspects on the owner home page to
 * their dedicated /agent token pages and resolves the URL each card can
 * safely link to.
 *
 * Two kinds of targets:
 *  - token-agnostic pages (profile, website, leads): they read their data
 *    through agent-action-respond, which accepts ANY active link token, so
 *    the URL token is reused as-is.
 *  - typed pages (photos, keywords, audit-report, ...): both the page
 *    (useAgentLink expectedType) and several agent-shareable-links actions
 *    enforce the link's own type, so they need a token OF THAT TYPE. The
 *    URL token is reused when its type already matches; otherwise a typed
 *    sibling token comes from agent-action-respond's page_links op, which
 *    mints server-side so an anonymous owner gets the same working buttons
 *    a signed-in member does.
 *
 * Nothing here reads or writes agent_shareable_links from the browser. A
 * member-only fallback used to, when page_links could not answer; its RLS
 * read gave owners who are not members, Fly staff and ops nothing, and when
 * page_links is down the same function is down, so it bought nothing (Anand,
 * 25 Sep 26, tests/agent/unit/settings-links-server-side.test.ts).
 *
 * search + performance have no page of their own on purpose: no /agent token
 * page reads search_insights or the weekly KPI rows (verified 17 Sep 26), so
 * both chips stay on the home page and scroll to the Reports card that holds
 * their numbers. See ASPECTS_WITHOUT_PAGE below.
 */

import { supabase } from '@/integrations/supabase/client';
import type { AgentCallerRef } from '@/lib/agentCaller';
import { appAddressWithPageWords, appPagePath } from '@/lib/appAddress';
import { hasAppTwin } from '@/pages/app/locations/pages';

export interface AgentPageTarget {
  /** URL segment under /agent, e.g. 'photos' -> /agent/photos/:token */
  path: string;
  /** Required agent_shareable_links.link_type, or null when any active token works. */
  linkType: string | null;
  /**
   * The query the page has to open with, e.g. 'step=write'. Added to the
   * address wherever this map is turned into a link, and to the answer the
   * page_links op gives for the same key, unless the address already names
   * that parameter.
   */
  query?: string;
}

/** Health aspect key -> dedicated /agent page. */
export const ASPECT_PAGE_LINKS: Record<string, AgentPageTarget> = {
  profile: { path: 'profile', linkType: null },
  website: { path: 'website', linkType: null },
  leads: { path: 'leads', linkType: null },
  seo: { path: 'audit-report', linkType: 'optimize' },
  reputation: { path: 'review-generator-setup', linkType: 'review_generator' },
  // Replies waiting for the owner are cards on the home page (Shardul, 17 Sep 26).
  reviews: { path: 'home', linkType: 'home' },
  review_settings: { path: 'review-responder-setup', linkType: 'review_responder' },
  // The page moved to /agent/createpost on 18 Sep 26. The old address still
  // renders it, so links already sent keep working; new ones use this.
  // New post opens on Write it, the step the owner starts on (Anand,
  // 24 Sep 26); the page reads ?step= and writes it back on every move.
  posts: { path: 'createpost', linkType: 'new_post', query: 'step=write' },
  // Drafts, scheduled and published, with the calendar. The server's
  // page_links has answered `ideas` all along; this map had no entry.
  ideas: { path: 'ideas', linkType: 'ideas' },
  photos: { path: 'photos', linkType: 'photos' },
  rank: { path: 'keywords', linkType: 'keywords' },
  // Directory listings, per directory (Anand, 18 Sep 26). Any active token opens it.
  citations: { path: 'listings', linkType: null },
  // Performance has its own page too (Anand, 18 Sep 26).
  performance: { path: 'performance', linkType: null },
  // Profile completeness rows (Anand, 18 Sep 26): services and attributes have pages.
  services: { path: 'gbp-services', linkType: 'gbp_services' },
  attributes: { path: 'gbp-attributes', linkType: 'gbp_attributes' },
  // Everything the agent has told the owner, with filters in the URL
  // (design flows.html, 19 Sep 26). Any active token opens it.
  notifications: { path: 'notifications', linkType: null },
  // Every content setting in one place (Anand, 28 Sep 26). Any page token
  // opens it; the server answers with the home token (page-links.ts).
  content_settings: { path: 'content-settings', linkType: null },
  // Posts, Post frames (walk round 2, item 59a). The mirror of page-links.ts:
  // a typed page, so a shared link waits for its own templates token.
  templates: { path: 'templates', linkType: 'templates' },
};

/**
 * Aspects that must NOT be turned into a page link, whatever the server
 * offers for them.
 *
 * agent-action-respond's page_links op answers with a target for every key
 * it knows, and its entry for search is the keyword rank tracker
 * (`search: { path: 'keywords', linkType: 'keywords' }`). The Search terms
 * chip shows how many times Google showed the profile in search last month;
 * the keywords page is a ranking table for tracked keywords. Two different
 * things, so the tap was landing the owner on numbers that had nothing to do
 * with the ones on the chip (QA, Shardul, 17 Sep 26). No token page reads
 * search_insights at all, so there is nowhere deeper to send them: the chip
 * scrolls to the Reports card that carries the same number instead.
 *
 * Performance sat here too while its numbers lived only in a dialog on the
 * home; since 18 Sep 26 it has its own page (/agent/performance), so it is
 * a link like the others.
 */
export const ASPECTS_WITHOUT_PAGE = ['search'] as const;

/** Drop the aspects whose only honest destination is the home page. */
function withoutPagelessAspects(hrefs: Record<string, string>): Record<string, string> {
  const cleaned = { ...hrefs };
  for (const key of ASPECTS_WITHOUT_PAGE) delete cleaned[key];
  return cleaned;
}

/**
 * One page's own query added to an address, before any anchor on it. An
 * address that already names the parameter is left as it is, so a link
 * carrying ?step=review is never dragged back to the first step.
 */
export function withQuery(href: string, query?: string | null): string {
  if (!href || !query) return href;
  const hash = href.indexOf('#');
  const base = hash >= 0 ? href.slice(0, hash) : href;
  const anchor = hash >= 0 ? href.slice(hash) : '';
  const mark = base.indexOf('?');
  const search = mark >= 0 ? base.slice(mark + 1) : '';
  const key = query.split('=')[0];
  if (search && new URLSearchParams(search).has(key)) return href;
  return `${base}${mark >= 0 ? '&' : '?'}${query}${anchor}`;
}

/** The queries ASPECT_PAGE_LINKS asks for, added to whatever resolved the hrefs. */
function withAspectQueries(hrefs: Record<string, string>): Record<string, string> {
  const out = { ...hrefs };
  for (const [key, target] of Object.entries(ASPECT_PAGE_LINKS)) {
    if (target.query && out[key]) out[key] = withQuery(out[key], target.query);
  }
  return out;
}

/** Editable profile areas linked from the public profile page. */
export const PROFILE_EDIT_PAGE_LINKS: Record<string, AgentPageTarget & { label: string }> = {
  // /agent/gbp-categories has existed and worked the whole time, but nothing
  // pointed at it: the More categories row sent the owner nowhere, and the
  // links card at the foot of the page did not list it either (Shardul,
  // 21 Sep 26).
  more_categories: { path: 'gbp-categories', linkType: 'gbp_categories', label: 'Categories' },
  services: { path: 'gbp-services', linkType: 'gbp_services', label: 'Products and services' },
  attributes: { path: 'gbp-attributes', linkType: 'gbp_attributes', label: 'Attributes and highlights' },
  photos: { path: 'photos', linkType: 'photos', label: 'Photos, logo and cover' },
  profile_changes: { path: 'profile-changes', linkType: 'profile_changes', label: 'Profile change history' },
};

export function agentPageUrl(path: string, token: string, query?: string | null): string {
  return withQuery(`/agent/${path}/${token}`, query);
}

const TOKEN_RE = /^[A-Za-z0-9]{8,64}$/;

/**
 * Every resolver below takes either the URL token (a token page) or the
 * caller (an app page, which has a location and no token). This turns the
 * first into the second so there is one shape inside.
 */
export type TokenOrCaller = string | AgentCallerRef;

function asCaller(who: TokenOrCaller | null | undefined): AgentCallerRef {
  if (typeof who === 'string') return { mode: 'token', token: who, locationId: null };
  return who ?? { mode: 'token', token: null, locationId: null };
}

/** The key one page_links answer is cached under: the token, or the location. */
function callerKey(caller: AgentCallerRef): string {
  return caller.mode === 'app' ? `app:${caller.locationId ?? ''}` : `token:${caller.token ?? ''}`;
}

/** True when this caller can ask at all. */
function callerUsable(caller: AgentCallerRef): boolean {
  return caller.mode === 'app' ? Boolean(caller.locationId) : Boolean(caller.token && TOKEN_RE.test(caller.token));
}

/** The body page_links is asked with, for whichever kind of caller this is. */
function pageLinksBody(caller: AgentCallerRef): Record<string, unknown> {
  return caller.mode === 'app'
    ? { op: 'page_links', location_id: caller.locationId }
    : { op: 'page_links', token: caller.token };
}

/**
 * The answer, in the addresses this caller should be sent to. A token page
 * keeps the token URLs the server built; an app page gets the same pages at
 * their /app addresses, so a signed-in owner never lands back on a link.
 */
function forCaller(caller: AgentCallerRef, hrefs: Record<string, string>): Record<string, string> {
  if (caller.mode !== 'app' || !caller.locationId) return hrefs;
  const own = tokensIn(hrefs);
  const out: Record<string, string> = {};
  for (const [key, href] of Object.entries(hrefs)) out[key] = rewriteLinkForApp(href, caller.locationId, own);
  return out;
}

/**
 * The address of a page for the caller looking at it: the token address on a
 * shared link, the app address for a signed-in owner (Anand, 23 Sep 26). Both
 * render the same component; only the way in differs. The app address is the
 * owner's clean one, /app/<slug>/<page>, once the slug is known (appPagePath,
 * clean addresses PR 3), else /app/l/<uuid>/<page>, which then moves.
 *
 * `token` is the token to use in token mode when it is not the caller's own,
 * which is how typed pages (photos, keywords, ...) are linked.
 */
export function pagePath(page: string, caller: AgentCallerRef | null, token?: string, query?: string | null): string {
  if (caller?.mode === 'app' && caller.locationId) return withQuery(appPagePath(caller.locationId, page), query);
  const useToken = token ?? caller?.token ?? '';
  return agentPageUrl(page, useToken, query);
}

/**
 * Where one ASPECT_PAGE_LINKS key sends this caller, query and all. The
 * resolvers below answer for every key at once; this is for a single link
 * that does not need the server, and for the tests that pin the addresses.
 */
export function aspectPageHref(key: string, caller: AgentCallerRef | null, token?: string): string | null {
  const target = ASPECT_PAGE_LINKS[key];
  if (!target) return null;
  return pagePath(target.path, caller, token, target.query);
}

/**
 * A token URL the server handed back, turned into its app address.
 *
 * The ops answer with /agent/<page>/<token> because that is what a card, an
 * email and a WhatsApp message all need. A signed-in owner tapping the same
 * card inside the app must not be dropped back onto a token URL, so the page
 * rewrites what it renders. Only page addresses are rewritten, and any query
 * string or anchor on them is kept.
 *
 * `ownTokens`, when given, is the set of tokens the home payload said belong
 * to this location: a link carrying any other token is left alone, because it
 * points somewhere else. Without it the match is on the path shape only.
 *
 * A link into the old /fly-app surface is turned around the same way (Anand,
 * 24 Sep 26: "ensure from nowhere, direct or referenced pages, /app sends the
 * user to /fly-app"). A location's manage page has an app page of its own, so
 * it goes there; anything else goes to the account home.
 */
export function rewriteLinkForApp(
  url: string | null | undefined,
  locationId: string | null | undefined,
  ownTokens?: Iterable<string>,
): string {
  const raw = typeof url === 'string' ? url : '';
  if (!raw || !locationId) return raw;

  // An absolute address on this same site counts; anywhere else is left alone.
  let path = raw;
  let prefix = '';
  const absolute = /^https?:\/\/[^/]+/.exec(raw);
  if (absolute) {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    if (!origin || absolute[0] !== origin) return raw;
    prefix = '';
    path = raw.slice(absolute[0].length) || '/';
  }

  // An old page that has an agent page of its own now (profile alerts, the
  // content ideas, the growth agent feed): that page, on this location.
  const legacy = legacyAgentPage(path);
  if (legacy) return withQuery(`${prefix}${appPagePath(locationId, legacy.page)}`, legacy.query);

  if (/^\/fly-app(?:[/?#]|$)/.test(path)) {
    const loc = /^\/fly-app\/locations\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})(?:[/?#].*)?$/.exec(path);
    return loc ? `${prefix}${appPagePath(loc[1], 'profile')}` : `${prefix}/app`;
  }

  // An app address the server answered with a page's old word (/app/<slug>/ideas): the page's
  // words now (PR 6), query and anchor kept, so a tap takes no move. Fly's own hosts only.
  const words = appAddressWithPageWords(path);
  if (words) return `${prefix}${words}`;

  const m = /^\/agent\/([^/?#]+)\/([A-Za-z0-9]{8,64})([/?#].*)?$/.exec(path);
  if (!m) return raw;
  const [, page, token, rest = ''] = m;
  // A token-only page (connect, reconnect, social-connect) has no /app route;
  // rewriting it sent the signed-in owner to a dead address (WO-14, 1 Oct 26).
  if (!hasAppTwin(page)) return raw;
  if (ownTokens) {
    const allowed = ownTokens instanceof Set ? ownTokens : new Set(ownTokens);
    if (!allowed.has(token)) return raw;
  }
  return `${prefix}${appPagePath(locationId, page, rest)}`;
}

/**
 * The agent page an old Fly address stands for now, or null when it has none.
 *
 * Notifications written before the agent pages existed still carry them
 * (T11, read on prod 01 Oct 26): the Profile Protection alerts link to
 * /fly-app/profile-alerts?location=..., the image alerts to
 * /content-ideas?idea=<id>, the old wrap-ups to /growth-agent. Each one opened
 * a page outside the location (or the account home), so each is mapped to the
 * agent page that holds the same thing: Change history, Outside Fly; the post;
 * Inbox, Activity. The location named in an old address is not used: the row
 * is read on the location it belongs to.
 */
export function legacyAgentPage(path: string): { page: string; query: string | null } | null {
  const m = /^(\/[^?#]*)(?:\?([^#]*))?/.exec(String(path ?? ''));
  if (!m) return null;
  const route = m[1].replace(/\/+$/, '');
  const params = new URLSearchParams(m[2] ?? '');
  if (route === '/fly-app/profile-alerts') return { page: 'profile-changes', query: 'who=outside' };
  if (route === '/content-ideas' || route === '/fly-app/content-ideas') {
    const idea = params.get('idea');
    return { page: 'ideas', query: idea && /^[0-9a-fA-F-]{36}$/.test(idea) ? `idea=${idea}` : null };
  }
  if (route === '/growth-agent') return { page: 'home', query: 'section=activity' };
  return null;
}

/**
 * Another location's agent home: its app address inside the app, its own home
 * token on a shared link. The switcher and the All locations roll-up both
 * send the owner across locations this way.
 */
export function locationHomePath(
  caller: AgentCallerRef | null,
  locationId: string | null | undefined,
  token: string | null | undefined,
): string {
  if (caller?.mode === 'app' && locationId) return appPagePath(locationId, 'home');
  return agentPageUrl('home', token ?? '');
}

/** Every token this location's page_links answer names, for rewriteLinkForApp. */
export function tokensIn(hrefs: Record<string, string> | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const href of Object.values(hrefs ?? {})) {
    const m = /^\/agent\/[^/?#]+\/([A-Za-z0-9]{8,64})/.exec(String(href ?? ''));
    if (m) out.add(m[1]);
  }
  return out;
}

/**
 * Synchronous part: pages reachable without asking the server anything.
 *
 * Inside the app that is every page in the map. An app address carries no
 * token, and the session says who is asking, so a typed page (photos,
 * keywords, the posts list) needs nothing minted for it; on a shared link the
 * typed pages still wait for a token of their own type.
 */
export function tokenAgnosticAspectHrefs(who: TokenOrCaller | undefined): Record<string, string> {
  const hrefs: Record<string, string> = {};
  const caller = asCaller(who);
  if (!callerUsable(caller)) return hrefs;
  const inApp = caller.mode === 'app';
  for (const [key, target] of Object.entries(ASPECT_PAGE_LINKS)) {
    if (inApp || !target.linkType) hrefs[key] = pagePath(target.path, caller, undefined, target.query);
  }
  return hrefs;
}

/** Profile-edit key -> the key the page_links op answers with. */
const PROFILE_EDIT_SERVER_KEYS: Record<string, string> = {
  more_categories: 'categories',
  services: 'services',
  attributes: 'attributes',
  photos: 'photos',
  profile_changes: 'profile_changes',
};

/**
 * The profile page's edit-page links, resolved the way the health strip
 * already resolves its own: through page_links on the server.
 *
 * The old resolver read agent_shareable_links straight from PostgREST, and
 * RLS gives the anon role nothing, so it returned {} for exactly the people
 * the token surface exists for. The Services, Attributes, Logo, Cover photo
 * and More categories rows therefore had no Edit button at all, and the links
 * card at the foot of the page rendered empty, while the pages themselves
 * worked fine (Shardul, 21 Sep 26).
 *
 * page_links runs with the service role, reuses a live sibling token where
 * one exists and mints one where it does not. When it cannot answer, the rows
 * go without an Edit button rather than reading the table from here (Anand,
 * 25 Sep 26).
 */
export async function resolveProfileEditHrefs(who: TokenOrCaller): Promise<Record<string, string>> {
  const hrefs: Record<string, string> = {};
  const caller = asCaller(who);
  if (!callerUsable(caller)) return hrefs;
  try {
    const { data, error } = await supabase.functions.invoke('agent-action-respond', {
      body: pageLinksBody(caller),
    });
    const links = (data as { links?: Record<string, string> } | null)?.links;
    if (!error && links) {
      for (const [key, serverKey] of Object.entries(PROFILE_EDIT_SERVER_KEYS)) {
        if (links[serverKey]) hrefs[key] = links[serverKey];
      }
      if (Object.keys(hrefs).length) return forCaller(caller, hrefs);
    }
  } catch {
    /* no Edit buttons this time; the pages themselves still open */
  }
  return hrefs;
}

/**
 * Full aspect -> URL map reachable from this caller: token-agnostic pages
 * immediately, typed pages once page_links has answered.
 */
// One page_links call per token, shared. The home page alone asked four
// times on every load (health strip, health cards, header, the page itself),
// and each answer is the same minted family (Shardul, 21 Sep 26).
//
// Inside the app the answer is per person since the clean addresses PR 2: it
// holds the signed-in person's own /app/<slug> addresses, keyed only by the
// location. So it is dropped whenever the person changes (resetAspectHrefs,
// from clearAppCaches in src/pages/app/appData.ts), the same rule as the
// slug map (PR 1b: only the signed-in person's slugs are held).
const aspectHrefsInFlight = new Map<string, Promise<Record<string, string>>>();

export function resolveAspectHrefs(who: TokenOrCaller): Promise<Record<string, string>> {
  const caller = asCaller(who);
  const key = callerKey(caller);
  const cached = aspectHrefsInFlight.get(key);
  if (cached) return cached.then((h) => ({ ...h }));
  const pending = resolveAspectHrefsUncached(caller);
  aspectHrefsInFlight.set(key, pending);
  // Only its own entry: a late failure of a call made before a reset must not drop the next person's.
  pending.catch(() => {
    if (aspectHrefsInFlight.get(key) === pending) aspectHrefsInFlight.delete(key);
  });
  return pending.then((h) => ({ ...h }));
}

/**
 * Sign out, or another person signed in (clearAppCaches): forget every
 * page_links answer held for this visit, so the next person's links are asked
 * for them and never carry the last person's slugs. Also a test seam.
 */
export function resetAspectHrefs(): void {
  aspectHrefsInFlight.clear();
}

/** The home token behind a page_links answer, or null when it gave none. */
export function homeTokenFrom(hrefs: Record<string, string>): string | null {
  const m = /^\/agent\/home\/([A-Za-z0-9]{8,64})/.exec(hrefs.home ?? '');
  return m ? m[1] : null;
}

/**
 * The token of this location's agent home, whatever kind of link `token` is.
 *
 * Reading the home page is allowed to every link, so a new-post, profile or
 * reconnect link can open it, but the home-only ops (my_locations, multi_list,
 * connect_link, work_summary, billing_get) refuse anything but a home link
 * with a 403. page_links answers `home` with the token itself when it already
 * is one, and with the live home link otherwise, minting it if needed. Falls
 * back to `token` when nothing better can be learned.
 */
export async function resolveHomeToken(token: string): Promise<string> {
  try {
    return homeTokenFrom(await resolveAspectHrefs(token)) ?? token;
  } catch {
    return token;
  }
}

/**
 * The home address for this caller: the app's own home inside the app, and
 * the location's home token page on a shared link.
 */
export async function resolveHomeHref(who: TokenOrCaller): Promise<string> {
  const caller = asCaller(who);
  if (caller.mode === 'app') return pagePath('home', caller);
  const token = caller.token ?? '';
  return agentPageUrl('home', await resolveHomeToken(token));
}

async function resolveAspectHrefsUncached(caller: AgentCallerRef): Promise<Record<string, string>> {
  const hrefs = tokenAgnosticAspectHrefs(caller);
  if (!callerUsable(caller)) return hrefs;

  // Server first: page_links works for every viewer, member or not, signed in
  // or on a link, and returns the whole family (including contacts, ideas,
  // references and the profile-edit pages) in one call, reusing live tokens
  // where they exist.
  try {
    const { data, error } = await supabase.functions.invoke('agent-action-respond', {
      body: pageLinksBody(caller),
    });
    const links = (data as { links?: Record<string, string> } | null)?.links;
    if (!error && links && Object.keys(links).length) {
      const served = forCaller(caller, links);
      // Reviews land on the cards, not the top of the page.
      if (served.reviews && !served.reviews.includes('#')) served.reviews = `${served.reviews}#next-moves`;
      return withAspectQueries(withoutPagelessAspects({ ...hrefs, ...served }));
    }
  } catch {
    /* the token-agnostic pages below still link; typed chips keep their dialogs */
  }

  return withAspectQueries(withoutPagelessAspects(hrefs));
}
